import React, { useMemo, useState, lazy, Suspense } from "react";
import { Tabs, Tab } from "react-bootstrap";

// Lazy load components outside render so React does not recreate them on every state change.
const PropertyInfo = lazy(() => import("../../pages/hms/components/PropertyInfo"));

const MenuList = lazy(() => import("./components/MenuList"));
const CategoryList = lazy(() => import("./components/CategoryList"));
const POSTax = lazy(() => import("./components/TaxList"));
const TableConfig = lazy(() => import("./components/TableConfig"));
const GeneralSettings = lazy(() => import("./components/GeneralSettings"));
const UserManagement = lazy(() => import("../../pages/hms/components/ManageUsers"));
const WaiterManagement = lazy(() => import("./components/ManageWaiters"));
const DigitalMenu = lazy(() => import("./components/DigitalMenuConfig"));

const CONFIG_TABS = [
  {
    key: "propertyInfo",
    title: "Outlet Information",
    icon: "fas fa-store",
    description: "Outlet profile, billing identity, contact details, and POS-facing business information.",
    component: PropertyInfo,
    group: "Profile",
    tone: "#2563eb",
  },
  {
    key: "MenuList",
    title: "Menu Master",
    icon: "fas fa-utensils",
    description: "Create and manage sellable menu items, pricing, tax allocation, availability, and item metadata.",
    component: MenuList,
    group: "Menu",
    tone: "#16a34a",
  },
  {
    key: "CategoryList",
    title: "Menu Categories",
    icon: "fas fa-layer-group",
    description: "Organize menu items into fast, cashier-friendly categories for KOT and billing screens.",
    component: CategoryList,
    group: "Menu",
    tone: "#0ea5e9",
  },
  {
    key: "POSTax",
    title: "Taxes",
    icon: "fas fa-percent",
    description: "Configure POS taxes, tax breakup rules, and item-level tax calculation behavior.",
    component: POSTax,
    group: "Finance",
    tone: "#f59e0b",
  },
  {
    key: "tables",
    title: "Manage Tables",
    icon: "fas fa-chair",
    description: "Manage restaurant floor tables, service areas, seat capacity, and operational table setup.",
    component: TableConfig,
    group: "Service",
    tone: "#7c3aed",
  },
  {
    key: "generalSettings",
    title: "Settings",
    icon: "fas fa-sliders-h",
    description: "Control billing, KOT, payment, print, delivery, and operational POS preferences.",
    component: GeneralSettings,
    group: "Settings",
    tone: "#475569",
  },
  {
    key: "manageUsers",
    title: "Users",
    icon: "fas fa-users",
    description: "Manage staff access, roles, permissions, module access, and user status.",
    component: UserManagement,
    group: "Access",
    tone: "#2563eb",
  },
  {
    key: "WaiterManagement",
    title: "Waiters",
    icon: "fas fa-user-tie",
    description: "Create and maintain waiter/server records for table service and KOT attribution.",
    component: WaiterManagement,
    group: "Service",
    tone: "#14b8a6",
  },

];

const LoadingSpinner = ({ label = "Loading POS configuration..." }) => (
  <div className="ns-pos-config-loader">
    <div className="ns-pos-config-loader-card">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <span>{label}</span>
    </div>
  </div>
);

const POSConfiguration = () => {
  const [activeTab, setActiveTab] = useState("propertyInfo");

  const activeConfig = useMemo(
    () => CONFIG_TABS.find((tab) => tab.key === activeTab) || CONFIG_TABS[0],
    [activeTab]
  );

  const ActiveComponent = activeConfig.component;

  const summaryStats = useMemo(
    () => [
      { label: "Menu", color: "#16a34a" },
      { label: "Tables", color: "#7c3aed" },
      { label: "KOT", color: "#2563eb" },
      { label: "Payments", color: "#f59e0b" },
    ],
    []
  );

  const renderTabTitle = (tab) => (
    <span className="ns-pos-config-tab-title">
      <i className={`${tab.icon} ns-pos-config-tab-icon`} />
      <span>{tab.title}</span>
    </span>
  );

  return (
    <div className="page-wrapper posmt-3">
      <style>{`
        .ns-pos-config-page {
          min-height: calc(100vh - 70px);
          background:
            radial-gradient(circle at 10% 0%, rgba(37, 99, 235, 0.08), transparent 30%),
            radial-gradient(circle at 92% 2%, rgba(20, 184, 166, 0.08), transparent 28%),
            #f8fafc;
          padding-bottom: 22px;
          margin-top : 55px;
        }

        .ns-pos-config-hero {
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.15), transparent 34%),
            radial-gradient(circle at top right, rgba(34, 197, 94, 0.10), transparent 32%),
            linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07);
          padding: 18px 20px;
          margin-bottom: 14px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 18px;
          flex-wrap: wrap;
        }

        .ns-pos-config-title-wrap {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          min-width: 0;
        }

        .ns-pos-config-main-icon {
          width: 48px;
          height: 48px;
          border-radius: 18px;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 16px 32px rgba(37, 99, 235, 0.22);
          flex: 0 0 auto;
          font-size: 1.08rem;
        }

        .ns-pos-config-eyebrow {
          color: #2563eb;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          font-weight: 950;
          margin-bottom: 5px;
        }

        .ns-pos-config-title {
          color: #001f5b;
          font-size: 1.42rem;
          line-height: 1.18;
          font-weight: 950;
          margin: 0;
        }

        .ns-pos-config-subtitle {
          color: #64748b;
          font-size: 0.86rem;
          line-height: 1.52;
          margin: 7px 0 0;
          max-width: 850px;
        }

        .ns-pos-config-quick-stats {
          display: flex;
          gap: 9px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .ns-pos-config-stat {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 999px;
          padding: 7px 11px;
          color: #0f172a;
          font-size: 0.78rem;
          font-weight: 950;
          box-shadow: 0 7px 18px rgba(15, 23, 42, 0.05);
          white-space: nowrap;
        }

        .ns-pos-config-stat span {
          width: 9px;
          height: 9px;
          border-radius: 999px;
          display: inline-block;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.08);
        }

        .ns-pos-config-active-strip {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.055);
          padding: 12px 14px;
          margin-bottom: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
        }

        .ns-pos-config-active-left {
          display: flex;
          align-items: center;
          gap: 11px;
          min-width: 0;
        }

        .ns-pos-config-active-icon {
          width: 40px;
          height: 40px;
          border-radius: 14px;
          background: var(--active-tone);
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
        }

        .ns-pos-config-active-title {
          color: #0f172a;
          font-size: 0.98rem;
          font-weight: 950;
          line-height: 1.18;
          margin: 0;
        }

        .ns-pos-config-active-desc {
          color: #64748b;
          font-size: 0.78rem;
          font-weight: 720;
          line-height: 1.42;
          margin: 3px 0 0;
        }

        .ns-pos-config-active-badge {
          border-radius: 999px;
          background: #f8fafc;
          color: #475569;
          border: 1px solid rgba(15, 23, 42, 0.08);
          padding: 7px 11px;
          font-size: 0.72rem;
          font-weight: 950;
          white-space: nowrap;
        }

        .ns-pos-config-shell {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 22px;
          box-shadow: 0 14px 38px rgba(15, 23, 42, 0.07);
          overflow: hidden;
        }

        .ns-pos-config-tabs .nav-tabs {
          background: #f8fafc;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          padding: 10px 12px 0;
          gap: 6px;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: thin;
        }

        .ns-pos-config-tabs .nav-tabs::-webkit-scrollbar {
          height: 7px;
        }

        .ns-pos-config-tabs .nav-tabs::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 999px;
        }

        .ns-pos-config-tabs .nav-tabs::-webkit-scrollbar-thumb {
          background: #93c5fd;
          border-radius: 999px;
        }

        .ns-pos-config-tabs .nav-tabs .nav-link {
          border: 1px solid transparent !important;
          border-radius: 14px 14px 0 0 !important;
          color: #475569;
          font-weight: 850;
          padding: 11px 14px;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          transition: all 0.18s ease;
          background: transparent;
          margin-bottom: 0;
        }

        .ns-pos-config-tabs .nav-tabs .nav-link:hover {
          color: #1d4ed8;
          background: #eff6ff;
          border-color: rgba(37, 99, 235, 0.10) !important;
        }

        .ns-pos-config-tabs .nav-tabs .nav-link.active {
          color: #1d4ed8 !important;
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
          border-bottom-color: #ffffff !important;
          box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.04);
        }

        .ns-pos-config-tab-title {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .ns-pos-config-tab-icon {
          font-size: 0.92rem;
          opacity: 0.9;
        }

        .ns-pos-config-content {
          background: #f8fafc;
          padding: 14px;
        }

        .ns-pos-config-content-inner {
          min-height: 380px;
        }

        /*
          Child pages already contain their own .content.container-fluid wrapper.
          These rules prevent double spacing and heavy nested page wrappers.
        */
        .ns-pos-config-content-inner > .content.container-fluid,
        .ns-pos-config-content-inner .content.container-fluid {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        .ns-pos-config-content-inner > .content.container-fluid {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }

        .ns-pos-config-content-inner .page-header:first-child,
        .ns-pos-config-content-inner .page-top-icons:first-child {
          margin-top: 0 !important;
        }

        .ns-pos-config-content-inner > .card,
        .ns-pos-config-content-inner .card:first-child {
          border-radius: 18px;
          border-color: rgba(15, 23, 42, 0.08);
          box-shadow: 0 8px 22px rgba(15, 23, 42, 0.045);
        }

        .ns-pos-config-loader {
          min-height: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }

        .ns-pos-config-loader-card {
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
          padding: 24px 28px;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          color: #64748b;
          font-weight: 900;
        }

        @media (max-width: 992px) {
          .ns-pos-config-hero {
            padding: 16px;
          }

          .ns-pos-config-title {
            font-size: 1.24rem;
          }

          .ns-pos-config-quick-stats {
            justify-content: flex-start;
          }

          .ns-pos-config-tabs .nav-tabs {
            padding-left: 8px;
            padding-right: 8px;
          }

          .ns-pos-config-tabs .nav-tabs .nav-link {
            padding: 10px 12px;
          }

          .ns-pos-config-active-strip {
            align-items: flex-start;
          }
        }
      `}</style>

      <div className="content container-fluid ns-pos-config-page">
        <div className="ns-pos-config-hero">
          <div className="ns-pos-config-title-wrap">
            <span className="ns-pos-config-main-icon">
              <i className="fas fa-cash-register" />
            </span>

            <div>
              <div className="ns-pos-config-eyebrow">POS Setup</div>
              <h2 className="ns-pos-config-title">POS Configuration</h2>
             
            </div>
          </div>

          
        </div>

        <div
          className="ns-pos-config-active-strip"
          style={{ "--active-tone": activeConfig.tone }}
        >
          <div className="ns-pos-config-active-left">
            <span className="ns-pos-config-active-icon">
              <i className={activeConfig.icon} />
            </span>

            <div>
              <h3 className="ns-pos-config-active-title">{activeConfig.title}</h3>
              <p className="ns-pos-config-active-desc">{activeConfig.description}</p>
            </div>
          </div>

          <span className="ns-pos-config-active-badge">{activeConfig.group}</span>
        </div>

        <div className="ns-pos-config-shell">
          <Tabs
            id="pos-config-tabs"
            activeKey={activeTab}
            onSelect={(k) => {
              if (k) setActiveTab(k);
            }}
            className="ns-pos-config-tabs"
            mountOnEnter
            unmountOnExit={false}
          >
            {CONFIG_TABS.map((tab) => (
              <Tab
                key={tab.key}
                eventKey={tab.key}
                title={renderTabTitle(tab)}
              />
            ))}
          </Tabs>

          <div className="ns-pos-config-content">
            <div className="ns-pos-config-content-inner">
              <Suspense fallback={<LoadingSpinner label={`Loading ${activeConfig.title}...`} />}>
                <ActiveComponent />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POSConfiguration;
